import { escapeHtml } from '../format.js';
import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent, MenuSection } from '../types.js';
import {
  FRESH_DRINKS_FAMILY,
  FRESH_DRINKS_FAMILY_NAME,
  FRESH_DRINKS_THEME,
  FRESH_DRINKS_VARIANTS,
  descLines,
  fdBrand,
  fdNames,
  fdPhoto,
  fdSharedCss,
  fdTitle,
} from './fresh-drinks-cards.js';
import {
  badge,
  bilingual,
  description,
  hasTitle,
  image,
  pagePhotos,
  price,
  sectionTitle,
  sellingPrice,
  variantLabel,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * Fresh Drinks, layout C: a wide colour banner across the top with the ribbon title and three
 * round drink photos, then compact rows in two columns: a round thumbnail, the name in the
 * set's colour, and every size with its price across one line.
 */

const S = '.mb-t-fresh-drinks-banner';

function sizesLine(ctx: RenderContext, item: MenuItemContent): string {
  if (!item.variants.length) return `<div class="fdb-prices">${sellingPrice(ctx, item, 'fdb-p')}</div>`;
  return `<div class="fdb-prices">${item.variants
    .map((v) => `<span class="fdb-sz"><small>${variantLabel(ctx, v.label, v.labelAr)}</small><span class="fdb-p" dir="ltr">${price(ctx, v.price)}</span></span>`)
    .join('')}</div>`;
}

function row(ctx: RenderContext, item: MenuItemContent): string {
  const badges = badge(ctx, item, 'fdb-badge');
  return [
    '<div class="fdb-row">',
    fdPhoto(ctx, item, 'fdb'),
    '<div class="fdb-text">',
    `<div class="fdb-names">${fdNames(ctx, item, 'fdb')}${badges ? `<span class="fdb-badges">${badges}</span>` : ''}</div>`,
    description(ctx, item, 'fdb-desc'),
    '</div>',
    sizesLine(ctx, item),
    '</div>',
  ].join('');
}

function block(ctx: RenderContext, b: MenuBlock): string {
  const head = hasTitle(b.section) ? `<div class="fdb-sec">${sectionTitle(ctx, b.section, 'fdb-sec-t')}</div>` : '';
  return `<div class="fdb-block">${head}${b.items.map((i) => row(ctx, i)).join('')}</div>`;
}

/** Row heights in em at the reference size. */
function columns(board: MenuBoardContent, portrait: boolean): MenuColumn[] {
  const both = board.settings.language === 'both';
  const rowHeight = (item: MenuItemContent) => {
    const names = 1.95 + (both && item.titleAr ? 1.75 : 0);
    const text = names + descLines(board, item, 36, 1) * 1.2;
    // Four sizes or more may wrap to a second line beside the name.
    const prices = item.variants.length > 3 ? 4 : 2;
    return Math.max(5, text, prices) + 1.75;
  };
  const column: MenuColumn = {
    capacity: portrait ? (both ? 70 : 78) : 46.5,
    blockCost: (section: MenuSection, items) =>
      (hasTitle(section) ? (both && section.titleAr ? 3.9 : 3.4) : 0) + items.reduce((sum, i) => sum + rowHeight(i), 0),
  };
  return portrait ? [column] : [column, column];
}

export const freshDrinksBanner: MenuTemplate = {
  id: 'fresh-drinks-banner',
  name: 'Fresh drinks · Banner list',
  description:
    'A wide colour banner with the ribbon title and three round drink photos, over two columns of compact rows with every size and price on one line.',
  defaultTheme: FRESH_DRINKS_THEME,
  variants: FRESH_DRINKS_VARIANTS,
  cuisines: ['drinks', 'cafe'],
  family: { id: FRESH_DRINKS_FAMILY, name: FRESH_DRINKS_FAMILY_NAME, layout: 'Banner list' },
  uses: { featured: false, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    return packColumns(groupBySection(board), columns(board, orientation === 'portrait'), { balance: true });
  },

  render(ctx) {
    const { board } = ctx;
    const photos = pagePhotos(ctx, 3)
      .map(
        (item, i) =>
          `<figure class="fdb-bub fdb-bub-${i}">${image(ctx, item.imageMediaId, 'fdb-bub-img')}<figcaption>${escapeHtml(ctx.showEn ? item.title : item.titleAr?.trim() || item.title)}</figcaption></figure>`,
      )
      .join('');
    const banner = [
      '<div class="fdb-banner">',
      `<div class="fdb-lead">${fdTitle(ctx, 'fdb', 9)}</div>`,
      `<div class="fdb-mid">${fdBrand(ctx, 'fdb')}${bilingual(ctx, board.brand.tagline, board.brand.taglineAr, 'fdb-tag')}</div>`,
      photos ? `<div class="fdb-bubs">${photos}</div>` : '',
      '</div>',
    ].join('');
    const empty = ctx.page.columns.every((c) => c.length === 0);
    const lists = empty
      ? '<div class="mb-empty">No drinks on this board yet</div>'
      : `<div class="fdb-cols" style="--cols:${ctx.page.columns.length}">${ctx.page.columns
          .map((c) => `<div class="fdb-col">${c.map((b) => block(ctx, b)).join('')}</div>`)
          .join('')}</div>`;
    return banner + lists;
  },

  css: `
${S}{padding:0}
${fdSharedCss(S, 'fdb')}
${S} .fdb-banner{position:relative;flex:none;height:18.5em;display:flex;align-items:center;gap:3em;padding:1.2em 4em 2.2em;
  background:var(--mb-primary);color:#fff;
  background-image:radial-gradient(circle at 18% 30%,rgba(255,255,255,.24),transparent 45%),
    radial-gradient(circle at 85% 120%,rgba(0,0,0,.22),transparent 50%),
    repeating-conic-gradient(from 0deg at 18% 50%,rgba(255,255,255,.05) 0 6deg,transparent 6deg 14deg);
  clip-path:polygon(0 0,100% 0,100% calc(100% - 2em),0 100%)}
${S}.mb-portrait .fdb-banner{height:auto;flex-direction:column;gap:1.6em;padding:2.4em 2.4em 4em}
${S} .fdb-lead{flex:0 1 auto;min-width:0;display:flex;flex-direction:column;align-items:center;gap:.6em}
${S} .fdb-ribbon{font-size:2.9em}
${S} .fdb-caps{font-size:6.6em}
${S} .fdb-caps.mb-2nd{font-size:2.4em}
${S} .fdb-brandname{font-size:1.3em}
${S} .fdb-logo{height:3.6em}
${S} .fdb-mid{flex:1 1 0;min-width:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.8em;text-align:center}
${S} .fdb-tag{font-size:1.15em;opacity:.92;max-width:22em;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
${S} .fdb-tag.mb-2nd{font-size:1.05em}
${S}.mb-portrait .fdb-mid{flex:none}
${S} .fdb-bubs{flex:none;min-width:0;display:flex;justify-content:flex-end;align-items:center;gap:2.2em}
${S}.mb-portrait .fdb-bubs{justify-content:center;width:100%}
${S} .fdb-bub{position:relative;margin:0;width:13em;height:13em;flex:none;border-radius:50%;border:.5em solid var(--mb-surface);
  box-shadow:0 .8em 1.8em rgba(0,0,0,.3);background:var(--mb-surface)}
${S} .fdb-bub-1{width:15em;height:15em}
${S}.mb-portrait .fdb-bub{width:12em;height:12em}
${S}.mb-portrait .fdb-bub-1{width:14em;height:14em}
${S} .fdb-bub-img{width:100%;height:100%;object-fit:cover;border-radius:50%;display:block}
${S} .fdb-bub figcaption{position:absolute;left:50%;bottom:-.9em;transform:translateX(-50%);background:var(--mb-accent);color:var(--mb-text);
  font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);text-transform:uppercase;font-size:1em;letter-spacing:.04em;
  padding:.2em .9em;border-radius:99em;white-space:nowrap;max-width:120%;overflow:hidden;text-overflow:ellipsis;box-shadow:0 .2em .5em rgba(0,0,0,.2)}
${S} .fdb-cols{flex:1;min-height:0;display:grid;grid-template-columns:repeat(var(--cols),minmax(0,1fr));gap:3.6em;padding:1.2em 4em 2em}
${S}.mb-portrait .fdb-cols{padding:1.2em 3em 3em}
${S} .fdb-col{min-width:0;display:flex;flex-direction:column;gap:.8em}
${S} .fdb-block{display:flex;flex-direction:column;gap:.8em}
${S} .fdb-sec{align-self:flex-start;background:var(--mb-primary);color:#fff;padding:.3em 2.4em .3em 1.1em;margin-top:.4em;
  clip-path:polygon(0 0,100% 0,calc(100% - 1.3em) 100%,0 100%);max-width:100%;display:flex;gap:.7em;align-items:baseline}
${S}[dir=rtl] .fdb-sec{clip-path:polygon(0 0,100% 0,100% 100%,1.3em 100%);padding:.3em 1.1em .3em 2.4em}
${S} .fdb-sec-t{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.7em;text-transform:uppercase;
  letter-spacing:.04em;line-height:1.15;white-space:nowrap}
${S} .fdb-sec-t.mb-2nd{font-size:1.4em}
${S} .fdb-row{display:grid;grid-template-columns:5em minmax(0,1fr) auto;gap:1.2em;align-items:center;padding-bottom:.8em;
  border-bottom:.12em dashed color-mix(in srgb,var(--mb-primary) 30%,transparent)}
${S} .fdb-block .fdb-row:last-child{border-bottom:0;padding-bottom:0}
${S} .fdb-ph{width:5em;height:5em;border-radius:50%;border:.25em solid var(--mb-surface);box-shadow:0 .25em .7em rgba(0,0,0,.18)}
${S} .fdb-text{min-width:0;display:flex;flex-direction:column;gap:.15em}
${S} .fdb-names{display:flex;flex-direction:column;align-items:flex-start}
${S} .fdb-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.65em;text-transform:uppercase;
  color:var(--mb-primary);line-height:1.15;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%}
${S} .fdb-name2{font-family:var(--mb-font-heading);font-size:1.3em;color:var(--mb-text);opacity:.8;white-space:nowrap;
  overflow:hidden;text-overflow:ellipsis;max-width:100%}
${S} .fdb-prices{display:flex;flex-wrap:wrap;justify-content:flex-end;align-items:baseline;gap:.3em 0;font-size:1.2em;white-space:nowrap;max-width:31em}
${S}.mb-portrait .fdb-prices{max-width:34em}
${S} .fdb-sz{display:inline-flex;align-items:baseline;gap:.35em}
${S} .fdb-sz small{font-size:.85em;opacity:.7;font-weight:500}
${S} .fdb-sz{padding:0 1em;border-inline-start:.12em solid color-mix(in srgb,var(--mb-text) 20%,transparent)}
${S} .fdb-sz:first-child{border-inline-start:0}
${S} .fdb-sz:last-child{padding-inline-end:0}
${S} .fdb-sz small{font-size:.8em}
${S} .fdb-p{font-weight:800}
${S} .fdb-prices>.fdb-p{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);color:var(--mb-text);font-size:1.25em}
${S} .fdb-p-old{opacity:.5;font-weight:700;margin:0 .35em}
${S} .fdb-text .mb-2nd,${S} .fdb-name2{text-align:left}
${S} .fdb-badges{margin-top:.15em}
${S} .fdb-names:has(.fdb-badges){flex-direction:row;flex-wrap:wrap;align-items:center;gap:0 .6em}
${S} .fdb-names:has(.fdb-badges) .fdb-name2{order:3;flex-basis:100%}
`,
};
